<script setup>
import { computed } from 'vue';
import { useI18n } from 'vue-i18n';
import { useDateLocale } from 'dashboard/composables/useTimeAgo';
import BaseBubble from './Base.vue';
import { useMessageContext } from '../provider.js';

const { t } = useI18n();
const { localeTag } = useDateLocale();
const { contentAttributes, content } = useMessageContext();

const od = computed(() => contentAttributes.value?.whatsappOrderDetails);
const items = computed(() => od.value?.order?.items || []);
const totalAmountCents = computed(() => od.value?.totalAmountCents);
const paymentLink = computed(() => od.value?.paymentLink);
const referenceId = computed(() => od.value?.referenceId);
const currency = computed(() => od.value?.currency || 'BRL');

const totalFmt = computed(() => {
  if (totalAmountCents.value == null) return null;
  return new Intl.NumberFormat(localeTag.value, {
    style: 'currency',
    currency: currency.value,
  }).format(totalAmountCents.value / 100);
});
</script>

<template>
  <BaseBubble
    class="overflow-hidden p-0 !bg-n-slate-3 !text-n-slate-12"
    data-bubble-name="cart-order"
  >
    <div class="w-72">
      <!-- Inner card: order details -->
      <div class="mx-2 mt-2 mb-1 rounded-lg bg-n-slate-4 overflow-hidden">
        <!-- Reference -->
        <div class="px-2 pt-2 pb-1.5">
          <p class="text-[11px] text-n-slate-9 leading-none">
            {{ t('CART_ORDER.BILLING_LABEL') }} {{ referenceId }}
          </p>
        </div>

        <!-- Items -->
        <template v-for="item in items" :key="item.retailerId">
          <hr class="border-dashed border-n-slate-6 my-0" />
          <div class="px-2 py-1.5">
            <p class="text-xs font-bold text-n-slate-12 leading-snug">
              {{ item.name }}
            </p>
            <p class="text-xs text-n-slate-9 mt-0.5">
              {{ t('CART_ORDER.QUANTITY_LABEL') }} {{ item.quantity }}
            </p>
          </div>
        </template>

        <!-- Total -->
        <hr class="border-dashed border-n-slate-6 my-0" />
        <div class="px-2 py-1.5 flex justify-between items-center">
          <span class="text-xs text-n-slate-11">{{
            t('CART_ORDER.TOTAL')
          }}</span>
          <span class="text-sm font-bold text-n-slate-12 tabular-nums">{{
            totalFmt
          }}</span>
        </div>
      </div>

      <!-- Seu carrinho + body (outside card, below it) -->
      <div class="px-2 py-1.5">
        <p class="text-sm font-semibold text-n-slate-12">
          {{ t('CART_ORDER.YOUR_CART') }}
        </p>
        <p v-if="content" class="text-xs text-n-slate-10 mt-0.5">
          {{ content }}
        </p>
      </div>

      <!-- Footer CTA -->
      <div v-if="paymentLink" class="border-t border-n-slate-6">
        <a
          :href="paymentLink"
          target="_blank"
          rel="noopener noreferrer"
          class="flex items-center justify-center gap-1.5 px-2 py-2 text-xs font-medium text-n-slate-11 hover:text-n-slate-12 hover:bg-n-slate-4 transition-colors"
        >
          <i class="i-lucide-external-link text-xs" />
          {{ t('CART_ORDER.OPEN_PAYMENT_LINK') }}
        </a>
      </div>
    </div>
  </BaseBubble>
</template>
